import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Grid,
  TextField,
  Button,
  Chip,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { getPlanBundle, upsertPlanBundle } from './planData';

const steps = ['Patient Context', 'Objectives', 'Activities & Interventions', 'Review & Create'];

const aiGoalSuggestions = [
  'Complete trial onboarding while minimizing life disruption',
  'Improve symptom readiness and confidence before first trial visit',
  'Coordinate clinical and community support around trial milestones'
];

const objectiveSuggestions = {
  clinical: [
    'Finish baseline labs and screening checklist',
    'Review consent details with clinical trial team'
  ],
  lifestyle: [
    'Stabilize transportation and caregiver coverage',
    'Set nutrition and wellness support routine'
  ]
};

const TrialPlanCreationWizardPage = () => {
  const navigate = useNavigate();
  const { planId } = useParams();
  const [activeStep, setActiveStep] = useState(0);

  const existingBundle = useMemo(() => {
    if (!planId) {
      return null;
    }
    return getPlanBundle(planId);
  }, [planId]);

  const existingGoal = existingBundle?.items?.find((item) => item.type === 'goal');
  const existingClinicalObjectives = existingBundle?.items?.filter(
    (item) => item.type === 'objective' && item.details?.objectiveCategory === 'clinical'
  ) || [];
  const existingLifestyleObjectives = existingBundle?.items?.filter(
    (item) => item.type === 'objective' && item.details?.objectiveCategory === 'lifestyle'
  ) || [];
  const existingActivity = existingBundle?.items?.find((item) => item.type === 'activity');
  const existingIntervention = existingBundle?.items?.find((item) => item.type === 'intervention');

  const isEditMode = Boolean(planId);

  const [trialFocus, setTrialFocus] = useState(existingBundle?.plan?.details?.trialFocus || existingGoal?.details?.focusArea || 'Breast cancer trial participation readiness');
  const [patientNeeds, setPatientNeeds] = useState(existingBundle?.plan?.details?.patientNeeds || existingGoal?.details?.patientNeeds || 'Need transportation support, clear prep timeline, and care-team communication plan.');
  const [selectedGoal, setSelectedGoal] = useState(existingGoal?.name || aiGoalSuggestions[0]);

  const [clinicalObjectives, setClinicalObjectives] = useState(
    existingClinicalObjectives.length > 0
      ? existingClinicalObjectives.map((item) => item.name)
      : [objectiveSuggestions.clinical[0]]
  );
  const [lifestyleObjectives, setLifestyleObjectives] = useState(
    existingLifestyleObjectives.length > 0
      ? existingLifestyleObjectives.map((item) => item.name)
      : [objectiveSuggestions.lifestyle[0]]
  );

  const [activityPlan, setActivityPlan] = useState(existingActivity?.name || 'Community partner posts weekly transport and appointment support activity updates.');
  const [interventionPlan, setInterventionPlan] = useState(existingIntervention?.name || 'Weekly symptom-monitoring intervention linked to both clinical and lifestyle objectives.');

  const canContinue = useMemo(() => {
    if (activeStep === 0) {
      return trialFocus.trim() && patientNeeds.trim();
    }
    if (activeStep === 1) {
      return selectedGoal.trim() && (clinicalObjectives.length > 0 || lifestyleObjectives.length > 0);
    }
    if (activeStep === 2) {
      return activityPlan.trim() && interventionPlan.trim();
    }
    return true;
  }, [activeStep, trialFocus, patientNeeds, selectedGoal, clinicalObjectives, lifestyleObjectives, activityPlan, interventionPlan]);

  const toggleObjective = (setFn, list, item) => {
    setFn(list.includes(item) ? list.filter((value) => value !== item) : [...list, item]);
  };

  const handleCreatePlan = () => {
    const timestamp = Date.now();
    const planIdentifier = planId || `plan-${timestamp}`;
    const goalId = `goal-${timestamp}`;

    const objectiveItems = [
      ...clinicalObjectives.map((objective, index) => ({
        id: `objective-clinical-${timestamp}-${index}`,
        type: 'objective',
        name: objective,
        createdBy: 'Patient + Clinical Team',
        managedBy: 'Clinical Trial Management Team',
        approvalStatus: 'In Collaborative Review',
        parentType: 'goal',
        parentId: goalId,
        participants: ['Patient', 'Clinical Trial Management Team', 'Nurse Navigator'],
        details: {
          objectiveCategory: 'clinical',
          measures: ['Readiness confirmed']
        }
      })),
      ...lifestyleObjectives.map((objective, index) => ({
        id: `objective-lifestyle-${timestamp}-${index}`,
        type: 'objective',
        name: objective,
        createdBy: 'Patient + Community Partner',
        managedBy: 'Clinical Trial Management Team',
        approvalStatus: 'In Collaborative Review',
        parentType: 'goal',
        parentId: goalId,
        participants: ['Patient', 'Community Health Partner', 'Social Worker'],
        details: {
          objectiveCategory: 'lifestyle',
          measures: ['Support plan activated']
        }
      }))
    ];

    const lifestyleObjective = objectiveItems.find((item) => item.details?.objectiveCategory === 'lifestyle');
    const fallbackObjective = objectiveItems[0];

    const activityItem = {
      id: `activity-${timestamp}`,
      type: 'activity',
      name: activityPlan,
      createdBy: 'Community Health Partner',
      managedBy: 'Clinical Trial Management Team',
      approvalStatus: 'Proposed',
      parentType: 'objective',
      parentId: (lifestyleObjective || fallbackObjective || { id: goalId }).id,
      participants: ['Patient', 'Community Health Partner', 'Clinical Trial Management Team'],
      details: {
        postedBy: 'Community Health Partner',
        schedule: ['Schedule to be refined collaboratively']
      }
    };

    const interventionItem = {
      id: `intervention-${timestamp}`,
      type: 'intervention',
      name: interventionPlan,
      createdBy: 'Clinical Trial Management Team',
      managedBy: 'Clinical Trial Management Team',
      approvalStatus: 'In Collaborative Review',
      parentType: 'plan',
      parentId: planIdentifier,
      participants: ['Patient', 'Clinical Trial Management Team', 'Community Health Partner'],
      details: {
        linkedObjectiveIds: objectiveItems.map((item) => item.id),
        interventionProtocol: interventionPlan
      }
    };

    const plan = {
      id: planIdentifier,
      name: selectedGoal || 'Patient Trial Participation Plan',
      createdBy: 'Patient (AI-assisted Wizard)',
      managedBy: 'Clinical Trial Management Team',
      approvalStatus: 'In Collaborative Review',
      participants: [
        'Patient',
        'Clinical Trial Management Team',
        'Pharma Company / Trial Sponsor',
        'Resource Providers / Community Health Partners'
      ],
      details: {
        trialFocus,
        patientNeeds
      }
    };

    const goalItem = {
      id: goalId,
      type: 'goal',
      name: selectedGoal,
      createdBy: 'Patient (AI-assisted Wizard)',
      managedBy: 'Clinical Trial Management Team',
      approvalStatus: 'In Collaborative Review',
      parentType: 'plan',
      parentId: planId,
      participants: ['Patient', 'Clinical Trial Management Team'],
      details: {
        focusArea: trialFocus,
        patientNeeds
      }
    };

    const items = [goalItem, ...objectiveItems, activityItem, interventionItem];

    upsertPlanBundle({ plan, items });
    navigate(`/plans/${planIdentifier}`);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Button variant="text" sx={{ mb: 2, fontWeight: 600 }} onClick={() => navigate('/dashboard')}>
        ← Back to Trial Planning
      </Button>

      <Card sx={{ borderRadius: 3, border: 1, borderColor: 'divider', boxShadow: 0 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom>Trial Plan Creation Wizard</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isEditMode
              ? 'Edit your patient-led, AI-assisted trial participation plan.'
              : 'Patient-led, AI-assisted shared care planning for trial participation.'}
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 3 }} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Trial planning focus"
                  value={trialFocus}
                  onChange={(e) => setTrialFocus(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Patient needs (AI context)"
                  value={patientNeeds}
                  onChange={(e) => setPatientNeeds(e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          {activeStep === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>AI Goal Suggestions</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {aiGoalSuggestions.map((goal) => (
                    <Chip
                      key={goal}
                      label={goal}
                      color={selectedGoal === goal ? 'primary' : 'default'}
                      onClick={() => setSelectedGoal(goal)}
                    />
                  ))}
                </Box>
                <TextField fullWidth label="Selected goal" value={selectedGoal} onChange={(e) => setSelectedGoal(e.target.value)} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Clinical Objectives</Typography>
                <FormGroup>
                  {objectiveSuggestions.clinical.map((obj) => (
                    <FormControlLabel
                      key={obj}
                      control={
                        <Checkbox
                          checked={clinicalObjectives.includes(obj)}
                          onChange={() => toggleObjective(setClinicalObjectives, clinicalObjectives, obj)}
                        />
                      }
                      label={obj}
                    />
                  ))}
                </FormGroup>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Lifestyle Objectives</Typography>
                <FormGroup>
                  {objectiveSuggestions.lifestyle.map((obj) => (
                    <FormControlLabel
                      key={obj}
                      control={
                        <Checkbox
                          checked={lifestyleObjectives.includes(obj)}
                          onChange={() => toggleObjective(setLifestyleObjectives, lifestyleObjectives, obj)}
                        />
                      }
                      label={obj}
                    />
                  ))}
                </FormGroup>
              </Grid>
            </Grid>
          )}

          {activeStep === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Activity plan (community partner posted)"
                  value={activityPlan}
                  onChange={(e) => setActivityPlan(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Intervention plan (linked to one or more objectives)"
                  value={interventionPlan}
                  onChange={(e) => setInterventionPlan(e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          {activeStep === 3 && (
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Plan Preview</Typography>
                <Typography variant="body2" color="text.secondary">Goal: {selectedGoal}</Typography>
                <Typography variant="body2" color="text.secondary">Clinical Objectives: {clinicalObjectives.join(' • ') || 'None selected'}</Typography>
                <Typography variant="body2" color="text.secondary">Lifestyle Objectives: {lifestyleObjectives.join(' • ') || 'None selected'}</Typography>
                <Typography variant="body2" color="text.secondary">Activity: {activityPlan}</Typography>
                <Typography variant="body2" color="text.secondary">Intervention: {interventionPlan}</Typography>
              </CardContent>
            </Card>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button disabled={activeStep === 0} onClick={() => setActiveStep((step) => step - 1)}>
              Back
            </Button>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {activeStep < steps.length - 1 ? (
                <Button variant="contained" onClick={() => setActiveStep((step) => step + 1)} disabled={!canContinue}>
                  Next
                </Button>
              ) : (
                <Button variant="contained" onClick={handleCreatePlan}>
                  {isEditMode ? 'Save Plan Changes' : 'Create Plan'}
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TrialPlanCreationWizardPage;
